import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import i18n from "i18next";
import { LuX, LuCopy, LuClapperboard } from "react-icons/lu";
import { getToken } from "../../../../common/environment_config.es6";
import VideoThumb from "../../timeline/IdeaTimeline/components/VideoThumb";
import { PreviewHost, PreviewThumb } from "../audio/video-director/ImagePreview";

const label = { fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase" };
const clamp = (n) => ({
  display: "-webkit-box",
  WebkitLineClamp: n,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
});
const thumb = {
  width: 64,
  height: 64,
  objectFit: "contain",
  borderRadius: 8,
  background: "#f3f4f6",
  flexShrink: 0,
};

const Row = ({ k, v }) =>
  v ? (
    <div style={{ display: "flex", gap: 8, fontSize: 12 }}>
      <span style={{ ...label, minWidth: 64 }}>{k}</span>
      <span style={{ color: "#111827", wordBreak: "break-word" }}>{v}</span>
    </div>
  ) : null;
Row.propTypes = { k: PropTypes.string, v: PropTypes.node };

const Sheet = ({ items }) => {
  const ready = items.filter((i) => i?.still?.url);
  const gallery = ready.map((i) => ({
    url: i.still.url,
    name: i.name,
    description: i.description,
  }));
  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
      {ready.map((i, n) => (
        <PreviewThumb key={n} preview={{ items: gallery, index: n }} clickable>
          <img src={i.still.url} alt={i.name || ""} title={i.name} style={thumb} />
        </PreviewThumb>
      ))}
    </div>
  );
};
Sheet.propTypes = { items: PropTypes.array };

const Shot = ({ shot, n }) => {
  const [open, setOpen] = useState(false);
  const clips = (shot.takes || []).filter((t) => t.url);
  const clipItems = clips.map((t, i) => ({
    url: t.url,
    kind: "video",
    name: `${i18n.t("library_shot")} ${n} · ${i + 1}`,
    description: shot.prompt,
  }));
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={label}>
        {i18n.t("library_shot")} {n}
      </span>
      <div style={{ fontSize: 12, color: "#111827", ...(open ? {} : clamp(3)) }}>{shot.prompt}</div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          alignSelf: "flex-start",
          border: "none",
          background: "none",
          color: "#6b21a8",
          fontSize: 11,
          padding: 0,
          cursor: "pointer",
        }}
      >
        {open ? i18n.t("library_less") : i18n.t("library_more")}
      </button>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {shot.keyframe?.url && (
          <PreviewThumb
            preview={{
              items: [
                {
                  url: shot.keyframe.url,
                  name: `${i18n.t("library_shot")} ${n}`,
                  description: shot.keyframe.prompt,
                },
              ],
              index: 0,
            }}
            clickable
          >
            <img src={shot.keyframe.url} alt="" style={thumb} />
          </PreviewThumb>
        )}
        {clips.map((t, i) => (
          <PreviewThumb key={i} preview={{ items: clipItems, index: i }} clickable>
            <div className="cmp-lazyimg" style={{ ...thumb, height: 64 }}>
              <VideoThumb url={t.url} className="cmp-lazyimg__img is-loaded" />
            </div>
          </PreviewThumb>
        ))}
      </div>
    </div>
  );
};
Shot.propTypes = { shot: PropTypes.object, n: PropTypes.number };

// Side-panel drawer (not a modal): generation provenance of one library file.
const FileDetailsDrawer = ({ file, fileType, durationText, onClose, onOpenDirector }) => {
  const raw = file.raw || {};
  const projectId = raw.metadata?.videoProjectId;
  // undefined = loading · null = not available
  const [project, setProject] = useState(undefined);

  useEffect(() => {
    if (!projectId) return undefined;
    let alive = true;
    const base = process.env.MW_API_URL || window.ideaLibraryRootUrl || "";
    fetch(`${base}api/video-director/projects/${encodeURIComponent(projectId)}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then((res) => (res.ok ? res.json() : null))
      .catch(() => null)
      .then((doc) => alive && setProject(doc || null));
    return () => {
      alive = false;
    };
  }, [projectId]);

  const bible = project?.bible;
  const modelText = [raw.provider, raw.model].filter(Boolean).join(" / ");

  return (
    <div
      className="library-myfiles-details"
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 30,
        background: "#fff",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <PreviewHost>
        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            padding: 12,
            display: "flex",
            flexDirection: "column",
            gap: 10,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ flex: 1, fontSize: 14, fontWeight: 600, ...clamp(2) }}>{file.name}</div>
            <button
              type="button"
              title={i18n.t("close")}
              onClick={onClose}
              style={{ border: "none", background: "none", cursor: "pointer" }}
            >
              <LuX size={16} />
            </button>
          </div>
          {file.thumbnail && fileType === "image" && (
            <PreviewThumb
              preview={{
                items: [{ url: file.thumbnail, name: file.name, description: raw.prompt }],
                index: 0,
              }}
              clickable
            >
              <img
                src={file.thumbnail}
                alt={file.name}
                style={{ ...thumb, width: "100%", height: 140 }}
              />
            </PreviewThumb>
          )}
          <Row k={i18n.t("library_type")} v={fileType && i18n.t(fileType)} />
          <Row
            k={i18n.t("library_date")}
            v={raw.createdAt && new Date(raw.createdAt).toLocaleString()}
          />
          <Row k={i18n.t("library_duration")} v={durationText} />
          <Row k={i18n.t("library_provider")} v={modelText} />
          {raw.prompt && (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={label}>{i18n.t("library_prompt")}</span>
                <button
                  type="button"
                  title={i18n.t("library_copy_prompt")}
                  onClick={() => navigator.clipboard?.writeText(raw.prompt)}
                  style={{ border: "none", background: "none", cursor: "pointer", padding: 0 }}
                >
                  <LuCopy size={13} />
                </button>
              </div>
              <div style={{ fontSize: 12, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {raw.prompt}
              </div>
            </div>
          )}
          {projectId && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 8,
                borderTop: "1px solid #e5e7eb",
                paddingTop: 10,
              }}
            >
              <span style={label}>{i18n.t("library_director")}</span>
              {project === undefined && <span style={{ fontSize: 12 }}>{i18n.t("loading")}</span>}
              {project === null && (
                <span style={{ fontSize: 12, color: "#6b7280" }}>
                  {i18n.t("library_project_unavailable")}
                </span>
              )}
              {project && (
                <>
                  <Row k={i18n.t("library_logline")} v={bible?.logline} />
                  {typeof bible?.look === "string" && bible.look && (
                    <div style={{ fontSize: 12, ...clamp(3) }} title={bible.look}>
                      {bible.look}
                    </div>
                  )}
                  {bible?.cast?.length > 0 && <Sheet items={bible.cast} />}
                  {bible?.locations?.length > 0 && <Sheet items={bible.locations} />}
                  {(project.shots || []).map((s, i) => (
                    <Shot key={i} shot={s} n={i + 1} />
                  ))}
                </>
              )}
            </div>
          )}
        </div>
        {project && (
          <div style={{ padding: 12, borderTop: "1px solid #e5e7eb", background: "#fff" }}>
            <button
              type="button"
              onClick={() => onOpenDirector(projectId)}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
                padding: "8px 12px",
                borderRadius: 999,
                border: "1px solid #7c3aed",
                background: "#f3e8ff",
                color: "#6b21a8",
                fontSize: 12,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              <LuClapperboard size={14} />
              {i18n.t("library_open_in_director")}
            </button>
          </div>
        )}
      </PreviewHost>
    </div>
  );
};

FileDetailsDrawer.propTypes = {
  file: PropTypes.object.isRequired,
  fileType: PropTypes.string,
  durationText: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onOpenDirector: PropTypes.func.isRequired,
};

export default FileDetailsDrawer;
